/**
 * # dxfBridge.textStyleRecord — the canonical STYLE record projection
 *
 * Single source of truth for turning a typed `TextStyle` into the dxf-io
 * `DxfTextStyleEntry` the writer serializes, and for CREATING a STYLE record
 * the export discovers late (an MLEADER / MLEADERSTYLE referencing a text
 * style name absent from `styleTables`). Lives outside `dxfBridge.export.ts`
 * so the MLEADER stampers (which export.ts imports) can share it without an
 * import cycle.
 *
 * Source of truth: Autodesk DXF reference STYLE group codes + ezdxf 1.4.3
 * `Textstyle.DEFAULT_ATTRIBS`.
 */
import type { DxfTextStyleEntry } from "@dxf-ai-editor/dxf-io";

import { textStyleSchema, type TextStyle } from "./model";

export function drawingTextStyleToDxfEntry(style: TextStyle): DxfTextStyleEntry {
  // T9b — emit the FULL canonical STYLE record. AutoCAD and ezdxf always write
  // `70 40 41 50 71 42 3 4` on every STYLE (Autodesk DXF reference STYLE group
  // codes; ezdxf `Textstyle.DEFAULT_ATTRIBS`), including the default `Standard`
  // (`70 0 / 40 0 / 41 1 / 50 0 / 71 0 / 42 2.5 / 3 txt / 4 <empty>`). The
  // previous "only when it differs from the default" projection emitted a bare
  // `2 Standard` record on EVERY path — a shape AutoCAD recovers rather than
  // opens. `bigFontFile` is passed even when empty because the writer emits `4`
  // only for a defined value. `fontCharset` (1071) stays conditional — AutoCAD
  // writes it only when a charset is set.
  const entry: DxfTextStyleEntry = {
    name: style.name,
    flags: style.flags,
    height: style.height,
    widthFactor: style.widthFactor,
    obliqueAngle: style.obliqueAngle,
    generationFlags: style.generationFlags,
    lastHeight: style.lastHeight,
    fontName: style.fontName,
    bigFontFile: style.bigFontFile,
  };
  if (style.fontCharset !== 0) entry.fontCharset = style.fontCharset;
  // T9b — the record's own group-5 + `330` owner (imported verbatim / minted
  // + wired by the conformant skeleton for a from-scratch doc).
  if (style.handle !== undefined) entry.handle = style.handle;
  if (style.ownerHandle !== undefined) entry.ownerHandle = style.ownerHandle;
  // ROOT E — thread subclass-marker presence (see drawingDimStyleToDxfEntry).
  if (style.hadSubclassMarker !== undefined) {
    entry.hadSubclassMarker = style.hadSubclassMarker;
  }
  return entry;
}

/**
 * T9b review round 1 (P2) — build the STYLE record for a text style NAME the
 * export references but `styleTables` does not carry. The record is derived
 * from the TYPED default (`textStyleSchema.parse({ name })` — the same shape a
 * freshly created style has) so it carries the full canonical payload
 * (`70 40 41 50 71 42 3 4`), never a bare `{ name }` + handle that ezdxf
 * silently completes on load. Its `330` owner is the STYLE table head — the
 * owner every sibling record in `siblings` already carries (taken from the
 * first sibling that has one; left unset when none does — never fabricated).
 * The own group-5 is NOT assigned here: the caller's collision-checked minter
 * stamps it, exactly like any other late-minted record.
 */
export function createMissingTextStyleEntry(
  name: string,
  siblings: readonly DxfTextStyleEntry[],
): DxfTextStyleEntry {
  const entry = drawingTextStyleToDxfEntry(textStyleSchema.parse({ name }));
  const owner = siblings.find((s) => s.ownerHandle !== undefined)?.ownerHandle;
  if (owner !== undefined) entry.ownerHandle = owner;
  return entry;
}
